import { Controller } from "@hotwired/stimulus"

// Updated terminal controller - v2.1 - 2024-01-20 15:30
export default class extends Controller {
  static targets = []

  connect() {
    console.log('NEW TERMINAL CONTROLLER v2.1 LOADED - Cache cleared!')
    
    // Force scroll to top when page first loads
    window.scrollTo({ top: 0, behavior: 'auto' });
    
    // Track user scroll behavior
    this.userHasScrolled = false;
    this.lastScrollTop = window.scrollY;
    
    // Listen for user scroll events
    window.addEventListener('scroll', () => {
      const currentScrollTop = window.scrollY;
      // If user scrolled manually (not programmatically), disable auto-scroll
      if (Math.abs(currentScrollTop - this.lastScrollTop) > 100) {
        this.userHasScrolled = true;
      }
      this.lastScrollTop = currentScrollTop;
    });
    
    // Define rotation examples
    this.examples = [
      {
        command: 'ciphero.verify("OpenAI\'s GPT-5 was released in March 2024 with 10 trillion parameters")',
        result: {
          status: "false_claim",
          statusClass: "false",
          statusIcon: "✗",
          confidence: 96,
          explanation: "As of 2024, OpenAI has not announced or released GPT-5. The latest model is GPT-4, and no official information about GPT-5's parameters or release date exists.",
          category: "AI Hallucination"
        }
      },
      {
        command: `ciphero.verify(\`
const query = "SELECT * FROM users WHERE id = " + req.params.id;
db.execute(query);
\`, { type: 'code' })`,
        result: {
          status: "critical_vulnerability",
          statusClass: "false",
          statusIcon: "⚠️",
          confidence: 99,
          explanation: "SQL Injection vulnerability detected. User input is directly concatenated into SQL query without sanitization. Use parameterized queries instead.",
          category: "Security Analysis",
          severity: "CRITICAL",
          vulnerabilities: ["SQL Injection", "No Input Validation"]
        }
      },
      {
        command: 'ciphero.verify("Bitcoin uses more energy than Argentina")',
        result: {
          status: "verified",
          statusClass: "true",
          statusIcon: "✓",
          confidence: 92,
          explanation: "Bitcoin's annual energy consumption (~120 TWh) does exceed Argentina's total electricity consumption (~125 TWh as of 2023).",
          category: "Environmental Claim"
        }
      },
      {
        command: 'ciphero.verify("GPT-4 has 1 trillion parameters")',
        result: {
          status: "uncertain",
          statusClass: "uncertain",
          statusIcon: "?",
          confidence: 45,
          explanation: "OpenAI has not officially disclosed GPT-4's parameter count. Estimates range from 200B to 1.7T parameters based on various analyses.",
          category: "Tech Claim"
        }
      }
    ];
    
    this.currentExample = 0;
    
    // Start matrix terminal typing effect first
    this.startMatrixTerminal()
    
    // Schedule smooth scroll to terminal after Cicero fade-in completes
    // Cicero animation: 3.5s duration + 0.3s delay = 3.8s total, add 0.2s buffer
    setTimeout(() => {
      this.scrollToTerminal()
    }, 4000)
    
    // Also add a backup animation start in case of timing issues
    setTimeout(() => {
      console.log('Backup animation check...')
      if (!document.querySelector('.terminal-command')) {
        console.log('No terminal animation detected, starting backup...')
        this.startTerminalDemo()
      }
    }, 10000)
    
    // Create particles for bottom section
    this.createBottomParticles()
  }
  
  // Matrix Terminal Typing Effect
  startMatrixTerminal() {
    console.log('Starting matrix terminal...')
    // Start the demo after terminal appears - reduced delay for testing
    setTimeout(() => {
      console.log('Starting terminal demo...')
      this.startTerminalDemo()
    }, 2000) // Reduced delay for testing
  }
  
  startTerminalDemo() {
    try {
      this.currentExample = 0
      this.showTerminalExample()
    } catch (error) {
      console.error('Error starting terminal demo:', error)
      // Fallback: just show a simple message
      const terminalText = document.getElementById('terminal-text')
      if (terminalText) {
        terminalText.innerHTML = '<span class="terminal-command">System ready. Welcome to Ciphero.</span>'
      }
    }
  }
  
  showTerminalExample() {
    const example = this.examples[this.currentExample]
    const terminalText = document.getElementById('terminal-text')
    const terminalMessages = document.getElementById('terminalMessages')
    
    console.log('Terminal elements found:', {
      terminalText: !!terminalText,
      terminalMessages: !!terminalMessages,
      example: example.command
    })
    
    if (!terminalText || !terminalMessages) {
      console.error('Terminal elements not found!')
      return
    }
    
    // Type the command
    this.typeCommand(example.command, () => {
      // After command is typed, first move it to history
      setTimeout(() => {
        this.moveCommandToHistory(example.command)
        
        // Then show processing
        setTimeout(() => {
          this.showTerminalProcessing()
          
          // Then show the result
          setTimeout(() => {
            this.showTerminalResult(example)
            
            // Move to next example after delay
            setTimeout(() => {
              this.currentExample = (this.currentExample + 1) % this.examples.length
              this.showTerminalExample()
            }, 4000)
          }, 2000)
        }, 800)
      }, 1000)
    })
  }
  
  typeCommand(command, callback) {
    const terminalText = document.getElementById('terminal-text')
    let charIndex = 0
    const typeSpeed = 50
    
    const typeChar = () => {
      if (charIndex < command.length) {
        terminalText.innerHTML = `<span class="terminal-command">${command.substring(0, charIndex + 1)}</span>`
        charIndex++
        // Scroll to bottom as we type
        this.scrollTerminalToBottom()
        setTimeout(typeChar, typeSpeed)
      } else {
        callback()
      }
    }
    
    // Clear text first
    terminalText.innerHTML = ''
    typeChar()
  }
  
  showTerminalProcessing() {
    const terminalMessages = document.getElementById('terminalMessages')
    const processingDiv = document.createElement('div')
    processingDiv.className = 'terminal-line'
    processingDiv.innerHTML = '<span class="terminal-output">Executing verification request...</span>'
    terminalMessages.appendChild(processingDiv)
    this.scrollTerminalToBottom()
    
    // Add some processing indicators
    setTimeout(() => {
      const indicatorDiv = document.createElement('div')
      indicatorDiv.className = 'terminal-line'
      indicatorDiv.innerHTML = '<span class="terminal-output">Analyzing with multiple verification engines...</span>'
      terminalMessages.appendChild(indicatorDiv)
      this.scrollTerminalToBottom()
    }, 500)
  }
  
  moveCommandToHistory(command) {
    const terminalText = document.getElementById('terminal-text')
    const terminalMessages = document.getElementById('terminalMessages')
    
    // Add command to history
    const commandDiv = document.createElement('div')
    commandDiv.className = 'terminal-line'
    commandDiv.innerHTML = `<span class="terminal-prompt">ciphero@matrix:~$</span> <span class="terminal-command">${command}</span>`
    terminalMessages.appendChild(commandDiv)
    this.scrollTerminalToBottom()
    
    // Clear the typing cursor after adding command to history
    terminalText.innerHTML = ''
  }

  showTerminalResult(example) {
    const terminalMessages = document.getElementById('terminalMessages')
    const result = example.result
    
    // Add result
    const resultDiv = document.createElement('div')
    resultDiv.className = `terminal-result ${result.statusClass}`
    
    let resultHtml = `
      <div class="flex items-center gap-2 mb-2">
        <span class="text-lg">${result.statusIcon}</span>
        <span class="font-bold">${result.status.replace(/_/g, ' ').toUpperCase()}</span>
      </div>
      <div class="terminal-confidence">Confidence: ${result.confidence}%</div>
      <div class="terminal-explanation">${result.explanation}</div>
    `
    
    if (result.vulnerabilities) {
      resultHtml += `
        <div class="mt-2">
          <div class="text-red-400 font-bold text-xs">VULNERABILITIES DETECTED:</div>
          <div class="text-red-300 text-xs">${result.vulnerabilities.join(', ')}</div>
        </div>
      `
    }
    
    resultDiv.innerHTML = resultHtml
    terminalMessages.appendChild(resultDiv)
    this.scrollTerminalToBottom()
    
    // Add separator
    const separatorDiv = document.createElement('div')
    separatorDiv.className = 'terminal-line terminal-separator mt-4'
    separatorDiv.innerHTML = '<span class="terminal-system">────────────────────────────────────────</span>'
    terminalMessages.appendChild(separatorDiv)
    
    // Scroll to bottom with delay to ensure content is rendered
    this.scrollTerminalToBottom()
  }

  // Create particles for the bottom emblem
  createBottomParticles() {
    const particlesContainer = document.querySelector('.bottom-particles')
    if (!particlesContainer) return
    
    for (let i = 0; i < 30; i++) {
      const particle = document.createElement('div')
      particle.className = 'bottom-particle'
      
      // Random positions around the emblem
      const angle = Math.random() * Math.PI * 2 // random angle
      const distance = 30 + Math.random() * 70   // random distance from center
      const x = 50 + Math.cos(angle) * distance  // convert to x coordinate (%)
      const y = 50 + Math.sin(angle) * distance  // convert to y coordinate (%)
      
      particle.style.left = `${x}%`
      particle.style.top = `${y}%`
      
      // Random sizes
      const size = Math.random() * 3 + 1
      particle.style.width = `${size}px`
      particle.style.height = `${size}px`
      
      // Random colors (blues to cyans)
      const hue = 180 + Math.random() * 40
      particle.style.backgroundColor = `hsl(${hue}, 80%, 70%)`
      
      // Random animations with different delays
      particle.style.animation = `
        fadeIn ${Math.random() + 0.5}s ease-out forwards,
        floatIn ${Math.random() * 3 + 2}s ease-out forwards,
        pulse ${Math.random() * 3 + 2}s infinite alternate
      `
      particle.style.animationDelay = `${4 + Math.random() * 2}s`
      
      particlesContainer.appendChild(particle)
    }
  }



  startVerificationAnimation() {
    // Create code lines animation - only if element exists
    const codeLines = document.querySelector('.code-lines')
    if (codeLines) {
      codeLines.innerHTML = '' // Clear previous lines
      for (let i = 0; i < 15; i++) {
        const line = document.createElement('div')
        line.className = 'code-line'
        line.style.top = `${(i * 7)}%`
        line.style.animationDelay = `${i * 0.2}s`
        line.style.animation = 'scanLine 3s ease-in-out infinite'
        line.style.animationDelay = `${i * 0.1}s`
        codeLines.appendChild(line)
      }
    }
    
    // Create AI particles - only if element exists
    const particles = document.querySelector('.ai-particles')
    if (particles) {
      particles.innerHTML = '' // Clear previous particles
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div')
        particle.className = 'ai-particle'
        // Random positions
        particle.style.left = `${Math.random() * 100}%`
        particle.style.top = `${Math.random() * 100}%`
        // Random sizes
        const size = Math.random() * 4 + 1
        particle.style.width = `${size}px`
        particle.style.height = `${size}px`
        // Random animations
        particle.style.animation = `floatIn ${Math.random() * 2 + 2}s ease-out forwards, pulse ${Math.random() * 2 + 1}s infinite alternate`
        particle.style.animationDelay = `${Math.random() * 2}s`
        particles.appendChild(particle)
      }
    }
  }

  scrollTerminalToBottom() {
    const terminalContent = document.getElementById('terminalContent')
    if (terminalContent) {
      // Use requestAnimationFrame to ensure DOM is updated before scrolling
      requestAnimationFrame(() => {
        terminalContent.scrollTop = terminalContent.scrollHeight
      })
    }
  }

  scrollToTerminal() {
    const terminalElement = document.querySelector('.matrix-terminal')
    if (terminalElement) {
      console.log('Scrolling to terminal after Cicero fade-in...')
      
      // Get header height to account for fixed header
      const header = document.querySelector('.app-header') || document.querySelector('header')
      const headerHeight = header ? header.offsetHeight : 80 // Default 80px if no header found
      
      // Calculate scroll position with header offset
      const elementPosition = terminalElement.offsetTop
      const offsetPosition = elementPosition - headerHeight - 20 // Add 20px extra margin
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    } else {
      console.log('Terminal element not found for auto-scroll')
    }
  }
} 